import clsx from 'clsx';
import { Loader2, Save } from 'lucide-react';

/**
 * Sticky bottom action bar — used on create/save flows so the primary
 * commit action is always reachable, even with a long form. Mirrors the
 * native-app pattern from the design reference: full-width amber button,
 * card radius, soft shadow, anchored above the iOS home indicator.
 *
 * Usage:
 *
 *   <form onSubmit={handleSubmit}>
 *     ... fields ...
 *     <SaveBar>
 *       <SaveButton type="submit" loading={submitting}>
 *         Save Expense
 *       </SaveButton>
 *     </SaveBar>
 *   </form>
 *
 * Important: pages that use SaveBar must add bottom padding to the main
 * content (e.g. `pb-28`) so the last form field isn't hidden behind the
 * bar. The bar is `position: fixed`, so it sits above content rather than
 * pushing it.
 */
export default function SaveBar({ children, className = '' }) {
  return (
    <div
      className={clsx(
        'fixed bottom-0 left-0 right-0 z-40 px-4 pt-3 pointer-events-none',
        className,
      )}
      // env() padding-bottom keeps the bar above the iOS home indicator;
      // the max() floor ensures consistent breathing room on Android too.
      style={{ paddingBottom: 'max(0.75rem, calc(env(safe-area-inset-bottom) + 0.5rem))' }}
    >
      <div className="pointer-events-auto">{children}</div>
    </div>
  );
}

/**
 * The canonical button rendered inside SaveBar. Larger than BigButton
 * (h-14 vs h-11) and shadow-elevated, so it reads as the page's primary
 * commit affordance even when content scrolls behind it.
 *
 *   - icon: Lucide icon component, defaults to <Save />. Pass null to omit.
 *   - loading: shows a spinner (replacing the icon) and disables clicks.
 *   - disabled: same as loading but without spinner.
 *   - type: defaults to 'submit' so it pairs cleanly with <form onSubmit>.
 */
export function SaveButton({
  icon: Icon = Save,
  loading = false,
  disabled = false,
  type = 'submit',
  className,
  children,
  ...rest
}) {
  const isDisabled = disabled || loading;
  return (
    <button
      type={type}
      disabled={isDisabled}
      aria-busy={loading || undefined}
      className={clsx(
        'w-full h-14 rounded-card shadow-lg flex items-center justify-center gap-2',
        'bg-amber-500 hover:bg-amber-600 active:bg-amber-600 text-white',
        'text-body font-semibold',
        'focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-300',
        'active:scale-[0.99] transition-transform',
        isDisabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer',
        className,
      )}
      {...rest}
    >
      {loading ? (
        <Loader2 className="h-5 w-5 animate-spin shrink-0" aria-hidden="true" />
      ) : Icon ? (
        <Icon className="h-5 w-5 shrink-0" aria-hidden="true" />
      ) : null}
      <span>{children}</span>
    </button>
  );
}
